import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { CalendarClock, DoorOpen, Plus, RefreshCw, Users, Video } from 'lucide-react'
import { ErpTable, Panel, SectionHeader, Td, Th } from '@/components/erp/primitives'
import { EmptyState, ErrorStrip, Forbidden, LoadingBlock } from '@/shared/ui/states'
import { Field, Modal } from '@/shared/ui/Modal'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { explain } from '@/shared/api/problem'
import { createMeeting, listMeetings } from './api'
import { meetingStatusLabel, whenLabel } from './vocab'

const POLL_MS = 15000

/**
 * The diary.
 *
 * It polls, slowly, for one reason: `waitingCount`. A chair who scheduled a meeting and
 * walked away needs to see that somebody is standing at the door, and fifteen seconds is
 * often enough for that without being a load on a list nobody is watching.
 */
export function MeetingsPage() {
  const canRead = useCan('meeting:read')
  const canCreate = useCan('meeting:create')
  const [tick, setTick] = useState(0)
  const [creating, setCreating] = useState(false)

  const { data, error, loading } = useLoad(() => listMeetings({}), [tick])
  const refresh = useCallback(() => setTick((t) => t + 1), [])

  useEffect(() => {
    const t = setInterval(refresh, POLL_MS)
    return () => clearInterval(t)
  }, [refresh])

  if (!canRead) return <Forbidden what="meetings" />

  const rows = data?.content ?? []

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Meetings"
        description="Video meetings, their lobby and their room."
        right={(
          <>
            <button type="button" className="erp-btn erp-btn-ghost h-9 text-erp-sm" onClick={refresh}>
              <RefreshCw className="size-4" /> Refresh
            </button>
            {canCreate && (
              <button type="button" className="erp-btn erp-btn-primary h-9 text-erp-sm"
                onClick={() => setCreating(true)}>
                <Plus className="size-4" /> Schedule
              </button>
            )}
          </>
        )}
      />

      <ErrorStrip message={error} />

      <Panel>
        {loading && !data ? <LoadingBlock label="Reading the diary…" /> : rows.length === 0 ? (
          <EmptyState
            title="No meetings yet"
            hint={canCreate
              ? 'Schedule one, then send people the join link.'
              : 'Nothing has been scheduled in your area.'}
          />
        ) : (
          <ErpTable head={(
            <tr>
              <Th>Meeting</Th>
              <Th>When</Th>
              <Th>Status</Th>
              <Th>At the door</Th>
              <Th> </Th>
            </tr>
          )}>
            {rows.map((meeting) => (
              <tr key={meeting.id}>
                <Td>
                  <Link to={`/meetings/${meeting.id}`} className="erp-link font-medium">
                    {meeting.title}
                  </Link>
                  {meeting.callerIsHost && (
                    <span className="ml-2 text-erp-xs text-muted-foreground">you chair this</span>
                  )}
                </Td>
                <Td>
                  <span className="inline-flex items-center gap-1.5">
                    <CalendarClock className="size-3.5 text-muted-foreground" />
                    {whenLabel(meeting.scheduledAt)}
                  </span>
                </Td>
                <Td>{meetingStatusLabel(meeting.statusCode)}</Td>
                <Td>
                  {/*
                    The number, not the names. Who is waiting is behind the host guard; that
                    somebody IS waiting is what makes a chair open the room.
                  */}
                  {meeting.waitingCount > 0 ? (
                    <span className="inline-flex items-center gap-1.5 text-amber-600">
                      <DoorOpen className="size-3.5" />
                      {meeting.waitingCount} waiting
                    </span>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </Td>
                <Td>
                  <Link to={`/meetings/${meeting.id}`} className="erp-btn erp-btn-ghost h-8 text-erp-sm">
                    {meeting.statusCode === 'LIVE' ? (
                      <><Video className="size-4" /> Join</>
                    ) : (
                      <><Users className="size-4" /> Open</>
                    )}
                  </Link>
                </Td>
              </tr>
            ))}
          </ErpTable>
        )}
      </Panel>

      {creating && (
        <ScheduleDialog onClose={() => setCreating(false)} onSaved={() => {
          setCreating(false)
          refresh()
        }} />
      )}
    </div>
  )
}

function ScheduleDialog({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [title, setTitle] = useState('')
  const [agenda, setAgenda] = useState('')
  const [scheduledAt, setScheduledAt] = useState(() => {
    // The next quarter hour: a meeting is almost never scheduled for right now, and a field
    // pre-filled with the past is a field everybody has to edit.
    const now = new Date(Date.now() + 15 * 60 * 1000)
    now.setMinutes(Math.ceil(now.getMinutes() / 15) * 15, 0, 0)
    return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16)
  })
  const [lobbyRequired, setLobbyRequired] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async () => {
    setBusy(true)
    setError(null)
    try {
      await createMeeting({
        title: title.trim(),
        agenda: agenda.trim() || null,
        scheduledAt: new Date(scheduledAt).toISOString(),
        lobbyRequired,
      })
      onSaved()
    } catch (e) {
      setError(explain(e))
      setBusy(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      width="md"
      title="Schedule a meeting"
      description="You chair it unless somebody else is named. The join link is issued afterwards."
      footer={(
        <div className="flex items-center justify-end gap-2">
          <button type="button" className="erp-btn erp-btn-ghost h-9 text-erp-sm" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="erp-btn erp-btn-primary h-9 text-erp-sm"
            disabled={busy || title.trim().length < 2} onClick={submit}>
            {busy ? 'Scheduling…' : 'Schedule'}
          </button>
        </div>
      )}
    >
      <div className="space-y-3">
        <ErrorStrip message={error} />

        <Field label="Title" required>
          <input className="erp-input h-9 w-full" value={title} maxLength={200} autoFocus
            onChange={(e) => setTitle(e.target.value)} placeholder="Strategy call" />
        </Field>

        <Field label="Agenda" hint="Optional. Shown to whoever opens the meeting, not to the lobby.">
          <textarea className="erp-input w-full" rows={3} value={agenda} maxLength={4000}
            onChange={(e) => setAgenda(e.target.value)} />
        </Field>

        <Field label="When">
          <input type="datetime-local" className="erp-input h-9 w-full" value={scheduledAt}
            onChange={(e) => setScheduledAt(e.target.value)} />
        </Field>

        <label className="flex items-start gap-2 text-erp-sm">
          <input type="checkbox" checked={lobbyRequired} className="mt-1"
            onChange={(e) => setLobbyRequired(e.target.checked)} />
          <span>
            <span className="font-medium">Hold people in a lobby</span>
            <span className="block text-erp-xs text-muted-foreground">
              Anyone with the link waits until you let them in. Turn this off only for a
              meeting whose link stays inside the building — without it, the link IS the
              admission.
            </span>
          </span>
        </label>
      </div>
    </Modal>
  )
}
